/**src\infrastructure\services\AuthService.ts
 * Authentication Service Implementation
 *
 * Implements IAuthService using HTTP client and storage.
 * Handles authentication logic and token management.
 */

import type {
  IAuthService,
  LoginCredentials,
  AuthResult,
} from '@domain/services';
import type { User } from '@domain/entities';
import { UserEntity } from '@domain/entities';
import type { IHttpClient, IStorage } from '@application/ports';
import type { UserDTO } from '@application/dto';
import { API_ENDPOINTS } from '@infrastructure/config';

const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

interface LoginResponse {
  success: boolean;
  user: UserDTO;
  token?: string;
  refreshToken?: string;
}

export class AuthService implements IAuthService {
  constructor(
    private readonly httpClient: IHttpClient,
    private readonly storage: IStorage
  ) { }


  async login(credentials: LoginCredentials): Promise<AuthResult> {
    console.log('BASE URL:', API_ENDPOINTS.auth.login);
    console.log('BODY:', {
      mail: credentials.email,
      contra: credentials.password,
    });

    const response = await this.httpClient.post<LoginResponse>(
      API_ENDPOINTS.auth.login,
      {
        mail: credentials.email,
        contra: credentials.password,
      }
    );

    const { success, user, token, refreshToken } = response.data;
    console.log('LOGIN RESPONSE:', response);
    console.log('LOGIN DATA:', response.data);

    // Store tokens
    if (token) {
      this.storage.setItem(AUTH_TOKEN_KEY, token);
    }
    if (refreshToken) {
      this.storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }

    return {
      success,
      user: this.mapToEntity(user),
      token,
      refreshToken,
    };
  }

  async logout(): Promise<void> {
    try {
      // Call logout endpoint if available
      await this.httpClient.post(API_ENDPOINTS.auth.logout);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear tokens
      this.storage.removeItem(AUTH_TOKEN_KEY);
      this.storage.removeItem(REFRESH_TOKEN_KEY);
    }
  }

  async refreshToken(token: string): Promise<AuthResult> {
    const response = await this.httpClient.post<LoginResponse>(
      API_ENDPOINTS.auth.refresh,
      { token }
    );

    const { user, token: newToken, refreshToken ,success} = response.data;

    // Update stored tokens
    if (newToken) {
      this.storage.setItem(AUTH_TOKEN_KEY, newToken);
    }
    if (refreshToken) {
      this.storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }

    return {
      success: success,
      user: this.mapToEntity(user),
      token: newToken,
      refreshToken,
    };
  }

  async verifyToken(token: string): Promise<boolean> {
    try {
      // You can implement token verification logic here
      // For example, call an endpoint or decode JWT
      if (!token) return false;
      return true;
    } catch {
      return false;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const token = this.storage.getItem<string>(AUTH_TOKEN_KEY);
      if (!token) {
        return null;
      }

      const response = await this.httpClient.get<UserDTO>(
        API_ENDPOINTS.auth.me
      );
      return this.mapToEntity(response.data);
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Map UserDTO to UserEntity (Domain)
   */
  private mapToEntity(dto: UserDTO): User {
    return new UserEntity(
      String(dto.id),
      dto.email,
      dto.name
    );
  }
}
